/**
 * MODULE: _shared/frontend-shell/src/layout/shellAssistant/ShellAssistantBottomBar.tsx
 * PURPOSE: Global Page | Assistant bottom bar — toggle inline dock without blocking main content [2.2.1340].
 * DEPENDENCIES: react-router-dom, @heroicons/react, ShellAssistantContext, shellAssistantLayout
 * PUBLIC_API: ShellAssistantBottomBar
 * SIDE_EFFECTS: Opens/collapses assistant dock session state
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 95/120
 * RECENT: 2026-06-09 [2.2.1360] — Slimmer tab bar styling aligned with compact dock chrome.
 * RECENT: 2026-06-08 [2.2.1344] — Slim segmented bar (36px), inline icon + label.
 */

import { SparklesIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useShellAssistant } from './ShellAssistantContext';
import { SHELL_ASSISTANT_BOTTOM_BAR_HEIGHT_PX } from './shellAssistantLayout';

export type ShellAssistantBottomBarProps = {
  pageTitle?: string;
  hidden?: boolean;
};

const tabBase =
  'relative flex h-7 min-w-0 flex-1 items-center justify-center gap-1 rounded-md px-2 text-[10px] font-medium leading-none transition-colors';

export function ShellAssistantBottomBar({ pageTitle, hidden }: ShellAssistantBottomBarProps) {
  const location = useLocation();
  const { session, collapseDock, openGlobalAssistant } = useShellAssistant();

  const pageContext = useMemo(
    () => ({
      path: `${location.pathname}${location.search}`,
      title: pageTitle,
    }),
    [location.pathname, location.search, pageTitle]
  );

  const assistantActive = session.dockExpanded && session.panelOpen;

  const showPage = useCallback(() => {
    if (assistantActive) collapseDock();
  }, [assistantActive, collapseDock]);

  const showAssistant = useCallback(() => {
    if (!assistantActive) openGlobalAssistant(pageContext);
  }, [assistantActive, openGlobalAssistant, pageContext]);

  if (hidden) return null;

  return (
    <nav
      className="z-30 flex shrink-0 items-center gap-1 border-t border-gray-200/60 bg-white/90 px-4 py-1 backdrop-blur-md dark:border-gray-700/50 dark:bg-gray-900/90 sm:px-6"
      aria-label="Page and assistant navigation"
      data-testid="shell-assistant-bottom-bar"
      style={{
        minHeight: SHELL_ASSISTANT_BOTTOM_BAR_HEIGHT_PX,
        paddingBottom: 'max(0.125rem, env(safe-area-inset-bottom, 0px))',
      }}
    >
      <button
        type="button"
        data-testid="shell-bottom-bar-page"
        aria-pressed={!assistantActive}
        aria-current={!assistantActive ? 'page' : undefined}
        onClick={showPage}
        className={[
          tabBase,
          !assistantActive
            ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200/90 dark:bg-gray-800 dark:text-gray-100 dark:ring-gray-600/80'
            : 'text-gray-500 hover:bg-white/70 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800/50 dark:hover:text-gray-200',
        ].join(' ')}
      >
        <Squares2X2Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate">Page</span>
      </button>
      <button
        type="button"
        data-testid="shell-bottom-bar-assistant"
        aria-pressed={assistantActive}
        aria-current={assistantActive ? 'page' : undefined}
        onClick={showAssistant}
        className={[
          tabBase,
          assistantActive
            ? 'bg-pink-50 text-pink-700 shadow-sm ring-1 ring-pink-200/90 dark:bg-pink-950/40 dark:text-pink-300 dark:ring-pink-800/60'
            : 'text-gray-500 hover:bg-pink-50/60 hover:text-pink-600 dark:text-gray-400 dark:hover:bg-pink-950/25 dark:hover:text-pink-300',
        ].join(' ')}
      >
        <SparklesIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate">Assistant</span>
      </button>
    </nav>
  );
}
